import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Calendar,
  Clock,
  User,
  Video,
  Phone,
  MessageCircle,
  Star,
  Eye,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  Pause,
  Play,
  FileText,
  Mic,
} from "lucide-react";
import { Session } from "@/types";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import Pagination from "@/components/ui/Pagination";
import { formatDate, formatTime, cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { exportSessionsToCSV } from "@/utils/csvExport";
import { useSessionRecording, useRemoteText } from "@/hooks/useSessionRecording";
import { ExportConfirmationDialog } from "@/components/ExportConfirmationDialog";
import { useToast } from "@/hooks/use-toast.tsx";

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case "completed":
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case "cancelled":
      return <XCircle className="h-4 w-4 text-red-500" />;
    case "no_show":
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    case "in_progress":
      return <Pause className="h-4 w-4 text-blue-500" />;
    case "scheduled":
      return <Clock className="h-4 w-4 text-gray-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-800";
    case "cancelled":
      return "bg-red-100 text-red-800";
    case "no_show":
      return "bg-orange-100 text-orange-800";
    case "in_progress":
      return "bg-blue-100 text-blue-800";
    case "scheduled":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getMediumIcon = (medium: string) => {
  switch (medium) {
    case "video":
      return <Video className="h-4 w-4" />;
    case "audio":
      return <Phone className="h-4 w-4" />;
    case "chat":
      return <MessageCircle className="h-4 w-4" />;
    default:
      return <Video className="h-4 w-4" />;
  }
};

// Safe formatting helpers
const toTitleCase = (input: string) => {
  return input.replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatSnakeCase = (value?: string | null, fallback = "-") => {
  if (!value || typeof value !== "string") return fallback;
  return value.replace(/_/g, " ");
};

const formatSnakeCaseToTitle = (value?: string | null, fallback = "-") => {
  if (!value || typeof value !== "string") return fallback;
  return toTitleCase(value.replace(/_/g, " "));
};

const SessionHistory: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterMedium, setFilterMedium] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [error, setError] = useState("");

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { showToast } = useToast();

  const sessionStatuses = [
    "scheduled",
    "in_progress",
    "completed",
    "cancelled",
    "no_show",
  ];

  const sessionMediums = ["video", "audio", "chat"];


  const fetchSessions = async (
    page = 1,
    search = "",
    status = "all",
    medium = "all",
    type = "all",
    startDate = "",
    endDate = ""
  ) => {
    try {
      setLoading(true);
      const params: any = {
        page,
        limit: 12,
      };

      if (search) params.search = search;
      if (status !== "all") params.status = status;
      if (medium !== "all") params.medium = medium;
      if (type !== "all") params.type = type;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      console.log("Fetching sessions with params:", params);

      // Use centralized API call
      const response = await api.get<{ data: Session[]; meta: any }>(
        `/admin/sessions?${new URLSearchParams(params)}`
      );
      console.log("Session API response:", response);

      // Handle the backend response structure
      setSessions(response.data || []);
      setTotalPages(response.meta?.lastPage || response.meta?.totalPages || 1);
      setTotalRecords(response.meta?.total || 0);
      setError("");
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
      setError(
        `Failed to load sessions: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      // Mock data for development
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions(
      currentPage,
      searchTerm,
      filterStatus,
      filterMedium,
      filterType,
      dateRange.start,
      dateRange.end
    );
  }, [
    currentPage,
    searchTerm,
    filterStatus,
    filterMedium,
    filterType,
    dateRange,
  ]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    switch (filterType) {
      case "status":
        setFilterStatus(value);
        break;
      case "medium":
        setFilterMedium(value);
        break;
      case "type":
        setFilterType(value);
        break;
    }
    setCurrentPage(1);
  };


  const handleViewSession = (session: Session) => {
    setSelectedSession(session);
    setShowDetailModal(true);
  };

  const handleViewChat = (session: Session) => {
    setSelectedSession(session);
    setShowChatModal(true);
  };

  const handleExportSessions = () => {
    if (totalRecords > 0) {
      setShowExportDialog(true);
    } else {
      showToast({
        type: "info",
        title: "No Sessions to Export",
        description:
          "There are no sessions to export for the current filter selection.",
      });
    }
  };

  const handleConfirmExport = async () => {
    setIsExporting(true);
    try {
      // Fetch all sessions with the current filters
      const params: any = {
        limit: totalRecords, // Fetch all records
        search: searchTerm,
        status: filterStatus,
        medium: filterMedium,
        type: filterType,
        startDate: dateRange.start,
        endDate: dateRange.end,
      };

      // Remove 'all' filters
      if (params.status === "all") delete params.status;
      if (params.medium === "all") delete params.medium;
      if (params.type === "all") delete params.type;
      if (!params.search) delete params.search;
      if (!params.startDate) delete params.startDate;
      if (!params.endDate) delete params.endDate;

      const response = await api.get<{ data: Session[]; meta: any }>(
        `/admin/sessions?${new URLSearchParams(params)}`
      );

      if (response.data && response.data.length > 0) {
        exportSessionsToCSV(response.data);
      } else {
        showToast({
          type: "info",
          title: "No Sessions Found",
          description: "No sessions found to export with the current filters.",
        });
      }
    } catch (error) {
      console.error("Failed to export sessions:", error);
      showToast({
        type: "error",
        title: "Export Failed",
        description: `Failed to export sessions: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      });
    } finally {
      setIsExporting(false);
      setShowExportDialog(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterMedium("all");
    setFilterType("all");
    setDateRange({ start: "", end: "" });
    setCurrentPage(1);
  };

  return (
    <div className="gap-y-6 flex flex-col">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Session History</h1>
        <button
          onClick={handleExportSessions}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 flex items-center space-x-2 transition-colors"
        >
          <Download className="h-4 w-4" />
          <span>Export</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col gap-4">
          {/* Basic Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search sessions, students, or counselors..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <select
                value={filterStatus}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Status</option>
                {sessionStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status
                      .replace("_", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </option>
                ))}
              </select>
              <select
                value={filterMedium}
                onChange={(e) => handleFilterChange("medium", e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Mediums</option>
                {sessionMediums.map((medium) => (
                  <option key={medium} value={medium}>
                    {medium.charAt(0).toUpperCase() + medium.slice(1)}
                  </option>
                ))}
              </select>
              {/* <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="text-sm">More Filters</span>
                <ChevronDown className={cn(
                  'h-4 w-4 transition-transform',
                  showAdvancedFilters && 'rotate-180'
                )} />
              </button> */}
            </div>
          </div>

          {/* Advanced Filters */}
          {/* {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <select
                value={filterMedium}
                onChange={(e) => handleFilterChange('medium', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Mediums</option>
                {sessionMediums.map((medium) => (
                  <option key={medium} value={medium}>
                    {medium.charAt(0).toUpperCase() + medium.slice(1)}
                  </option>
                ))}
              </select>
              
              <select
                value={filterType}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Types</option>
                {sessionTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
              
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => handleDateRangeChange('start', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Start Date"
              />
              
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => handleDateRangeChange('end', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="End Date"
              />
            </div>
          )} */}

          {/* Clear Filters */}
          {(searchTerm ||
            filterStatus !== "all" ||
            filterMedium !== "all" ||
            filterType !== "all" ||
            dateRange.start ||
            dateRange.end) && (
            <div className="flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      {/* Sessions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-12 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No sessions found
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ||
              filterStatus !== "all" ||
              filterMedium !== "all" ||
              filterType !== "all" ||
              dateRange.start ||
              dateRange.end
                ? "Try adjusting your search or filter criteria."
                : "Sessions will appear here once they are scheduled."}
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-gray-200 md:hidden">
              {sessions.map((session) => (
                <li key={session.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {getMediumIcon(session.medium)}
                      <span className="text-sm text-gray-900 capitalize truncate">
                        {session.medium} Session
                      </span>
                    </div>
                    <span
                      className={cn(
                        "shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                        getStatusColor(session.status),
                      )}
                    >
                      {getStatusIcon(session.status)}
                      <span className="ml-1 capitalize">
                        {formatSnakeCase(session.status, "unknown")}
                      </span>
                    </span>
                  </div>

                  <div className="text-xs text-gray-600 space-y-1">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3 text-gray-400 shrink-0" />
                      <span className="font-medium">Student:</span>
                      <span className="truncate">{session.studentName}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3 text-gray-400 shrink-0" />
                      <span className="font-medium">Counselor:</span>
                      <span className="truncate">{session.counselorName}</span>
                    </div>
                    {session.agencyName && (
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Agency:</span>
                        <span className="truncate">{session.agencyName}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span>{session.duration ? formatDuration(session.duration) : "-"}</span>
                    {session.rating ? (
                      <span className="flex items-center gap-1">
                        <Star className="h-3 w-3 text-yellow-400 fill-current" />
                        {session.rating.toFixed(1)}
                      </span>
                    ) : null}
                    {session.amount && <span>${session.amount}</span>}
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      onClick={() => handleViewSession(session)}
                      className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50"
                      aria-label="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {session.medium === "chat" && (
                      <button
                        onClick={() => handleViewChat(session)}
                        className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50"
                        aria-label="View Chat Messages"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Session Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Participants
                    </th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Schedule
                    </th> */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sessions.map((session) => (
                    <tr key={session.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            {getMediumIcon(session.medium)}
                          </div>
                          <div className="ml-3">
                            {/* <div className="text-sm font-medium text-gray-900">
                              {formatSnakeCaseToTitle(session.type, 'Unknown')}
                            </div> */}
                            <div className="text-sm text-gray-900 capitalize">
                              {session.medium} Session
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center mb-1">
                            <User className="h-3 w-3 mr-1 text-gray-400" />
                            <span className="font-medium">Student:</span>
                            <span className="ml-1">{session.studentName}</span>
                          </div>
                          <div className="flex items-center mb-1">
                            <User className="h-3 w-3 mr-1 text-gray-400" />
                            <span className="font-medium">Counselor:</span>
                            <span className="ml-1">
                              {session.counselorName}
                            </span>
                          </div>
                          {session.agencyName && (
                            <div className="flex items-center">
                              <span className="h-3 w-3 mr-1" />
                              <span className="font-medium">Agency:</span>
                              <span className="ml-1 text-gray-600">
                                {session.agencyName}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center mb-1">
                            <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                            <span>{formatDate(session.startedAt)}</span>
                          </div>
                          <div className="flex items-center">
                            <Clock className="h-3 w-3 mr-1 text-gray-400" />
                            <span>{formatTime(session.startedAt)}</span>
                          </div>
                        </div>
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {session.duration
                            ? formatDuration(session.duration)
                            : "-"}
                        </div>
                        {session.endedAt && (
                          <div className="text-sm text-gray-500">
                            Ended: {formatTime(session.endedAt)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                            getStatusColor(session.status)
                          )}
                        >
                          {getStatusIcon(session.status)}
                          <span className="ml-1 capitalize">
                            {formatSnakeCase(session.status, "unknown")}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {session.rating ? (
                          <div className="flex items-center">
                            <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                            <span className="text-sm font-medium text-gray-900">
                              {session.rating.toFixed(1)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                        {session.amount && (
                          <div className="text-sm text-gray-500">
                            ${session.amount}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewSession(session)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {session.medium === "chat" && (
                            <button
                              onClick={() => handleViewChat(session)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded"
                              title="View Chat Messages"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                total={totalRecords}
                pageSize={12}
                itemLabel="sessions"
              />
            </div>
          </>
        )}
      </div>

      {/* Session Detail Modal */}
      {showDetailModal && selectedSession && (
        <SessionDetailModal
          isOpen={showDetailModal}
          onClose={() => setShowDetailModal(false)}
          session={selectedSession}
        />
      )}

      {/* Chat Messages Modal */}
      {showChatModal && selectedSession && (
        <ChatMessagesModal
          isOpen={showChatModal}
          onClose={() => setShowChatModal(false)}
          session={selectedSession}
        />
      )}

      {/* Export Confirmation Dialog */}
      <ExportConfirmationDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onConfirm={handleConfirmExport}
        sessionCount={totalRecords}
        isExporting={isExporting}
      />
    </div>
  );
};

// Session Detail Modal Component
interface SessionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: Session;
}

const SessionDetailModal: React.FC<SessionDetailModalProps> = ({
  isOpen,
  onClose,
  session,
}) => {
  const isAudioVideo = session.medium === "video" || session.medium === "audio";

  const { data: recordingData, loading: recordingLoading, error: recordingError } =
    useSessionRecording(session.id, isOpen && isAudioVideo);

  const { text: summaryText, loading: summaryLoading } =
    useRemoteText(recordingData?.summary ?? null);

  const { text: transcriptText, loading: transcriptLoading } =
    useRemoteText(recordingData?.transcript?.txt ?? null);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className={cn("bg-white rounded-lg w-full max-h-[90vh] overflow-y-auto", isAudioVideo ? "max-w-4xl" : "max-w-2xl")}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Session Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session Type
                </label>
                <p className="text-sm text-gray-900">
                  {formatSnakeCaseToTitle(session.type, "Unknown")}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Medium
                </label>
                <div className="flex items-center">
                  {getMediumIcon(session.medium)}
                  <span className="ml-2 text-sm text-gray-900 capitalize">
                    {session.medium}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Student
                </label>
                <p className="text-sm text-gray-900">{session.studentName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Counselor
                </label>
                <p className="text-sm text-gray-900">{session.counselorName}</p>
              </div>
              {session.agencyName && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agency
                  </label>
                  <p className="text-sm text-gray-900">{session.agencyName}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <span
                  className={cn(
                    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                    getStatusColor(session.status)
                  )}
                >
                  {getStatusIcon(session.status)}
                  <span className="ml-1 capitalize">
                    {formatSnakeCase(session.status, "unknown")}
                  </span>
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <p className="text-sm text-gray-900">
                  {formatDate(session.startedAt)} at{" "}
                  {formatTime(session.startedAt)}
                </p>
              </div>
              {session.endedAt && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <p className="text-sm text-gray-900">
                    {formatDate(session.endedAt)} at{" "}
                    {formatTime(session.endedAt)}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duration
                </label>
                <p className="text-sm text-gray-900">
                  {formatDuration(session.duration)}
                </p>
              </div>
              {session.amount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <p className="text-sm text-gray-900">${session.amount}</p>
                </div>
              )}
            </div>

            {session.status === "completed" && (
              <>
                {session.rating && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rating
                    </label>
                    <div className="flex items-center">
                      <Star className="h-5 w-5 text-yellow-400 fill-current mr-1" />
                      <span className="text-sm font-medium text-gray-900 mr-2">
                        {session.rating.toFixed(1)}
                      </span>
                      <span className="text-sm text-gray-500">out of 5</span>
                    </div>
                  </div>
                )}

                {session.feedback && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Feedback
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      "{session.feedback}"
                    </p>
                  </div>
                )}

                {session.summary && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Session Summary
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      {session.summary}
                    </p>
                  </div>
                )}

                {session.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                      {session.notes}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Recording & Transcript Section (audio/video only) */}
            {isAudioVideo && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                  <Play className="h-4 w-4 mr-2 text-indigo-600" />
                  Recording & Transcript
                </h3>

                {recordingLoading ? (
                  <div className="flex items-center space-x-2 text-sm text-gray-500 py-4">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
                    <span>Loading recording data...</span>
                  </div>
                ) : recordingError ? (
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                    Failed to load recording data. Please try again.
                  </div>
                ) : !recordingData?.recordingUrl && !recordingData?.isRecorded ? (
                  <p className="text-sm text-gray-500 italic">
                    No recording available for this session.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {/* Media Player */}
                    {recordingData?.recordingUrl ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Recording
                        </label>
                        {session.medium === "video" ? (
                          <video
                            controls
                            className="w-full rounded-md bg-black max-h-64"
                            src={recordingData.recordingUrl}
                            preload="metadata"
                          >
                            Your browser does not support video playback.
                          </video>
                        ) : (
                          <audio
                            controls
                            className="w-full"
                            src={recordingData.recordingUrl}
                            preload="metadata"
                          >
                            Your browser does not support audio playback.
                          </audio>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        Recording is being processed. Check back later.
                      </p>
                    )}

                    {/* AI Summary */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <FileText className="h-3.5 w-3.5 mr-1.5" />
                        AI Summary
                      </label>
                      {summaryLoading ? (
                        <div className="animate-pulse h-16 bg-gray-100 rounded-md" />
                      ) : summaryText ? (
                        <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap max-h-48 overflow-y-auto">
                          {summaryText}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic">
                          No AI summary available.
                        </p>
                      )}
                    </div>

                    {/* Transcript */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Mic className="h-3.5 w-3.5 mr-1.5" />
                        Transcript
                      </label>
                      {transcriptLoading ? (
                        <div className="animate-pulse h-32 bg-gray-100 rounded-md" />
                      ) : transcriptText ? (
                        <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">
                          {transcriptText}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic">
                          No transcript available.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="text-sm text-gray-500 pt-4 border-t border-gray-200">
              <p>Session ID: {session.id}</p>
              <p>Created: {formatDate(session.createdAt)}</p>
              <p>Updated: {formatDate(session.updatedAt)}</p>
            </div>
          </div>

          <div className="flex justify-end pt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Chat Messages Modal Component
interface ChatMessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: Session;
}

const ChatMessagesModal: React.FC<ChatMessagesModalProps> = ({
  isOpen,
  onClose,
  session,
}) => {
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && session.medium === "chat") {
      fetchChatMessages();
    }
  }, [isOpen, session]);

  const fetchChatMessages = async () => {
    try {
      setLoading(true);
      // Always use admin endpoint for messages (admin has permission bypass)
      const rows = await api.get<any[]>(`/admin/sessions/${session.id}/messages`);
      const mapped = (rows || []).map((r: any) => ({
        id: r.id,
        sender: r.sender,
        message: r.message,
        timestamp: r.timestamp || r.sentAt || r.createdAt,
        type: r.senderId === session.studentId ? 'student' : 'counselor',
      }));
      setChatMessages(mapped);
    } catch (error) {
      console.error('Failed to fetch chat messages:', error);
      setChatMessages([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className="bg-white rounded-lg max-w-[80ch] w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Chat Messages
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {session.studentName} ↔ {session.counselorName} · {formatDate(session.startedAt)}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner size="md" />
            </div>
          ) : chatMessages.length === 0 ? (
            <div className="text-center text-gray-500 h-32 flex items-center justify-center">
              <div>
                <MessageCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>No chat messages found for this session.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {chatMessages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    "flex",
                    message.type === "student" ? "justify-start" : "justify-end"
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[60ch] px-4 py-2 rounded-lg",
                      message.type === "student"
                        ? "bg-gray-100 text-gray-900"
                        : "bg-primary text-white"
                    )}
                  >
                    <div className="text-xs font-medium mb-1">
                      {message.sender}
                    </div>
                    <div className="text-sm">{message.message}</div>
                    <div className="text-xs mt-1 opacity-75">
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionHistory;
